import { z } from "zod";

export const SessionQuestionSchema = z
  .object({
    session_question_id: z.string(),
    question_id: z.string().optional(),
    question_text: z.string(),
    sequence: z.string().optional(),
    slug: z.string().optional(),
    title: z.string().nullable().optional(),
    source: z.string().optional(),
    status: z.string().optional(),
    user_answer: z.string().nullable().optional(),
    persona: z.string().nullable().optional(),
    metadata: z.record(z.any()).optional(),
    extracted_answers: z.array(z.unknown()).optional(),
    skip: z.boolean().optional(),
    coverage_tags: z.array(z.string()).optional(),
    clarification_required: z.boolean().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional()
  })
  .passthrough();

const CoverageStatusSchema = z
  .object({
    domain_scores: z.record(z.unknown()).optional(),
    required_met: z.boolean().optional(),
    missing_domains: z.array(z.string()).optional()
  })
  .partial();

export const SessionSnapshotSchema = z
  .object({
    session_id: z.string(),
    state_version: z.number().optional(),
    status: z.string().optional(),
    active_question_id: z.string().nullable().optional(),
    questionnaire_state: z.record(z.unknown()).optional(),
    coverage_status: CoverageStatusSchema.optional(),
    questions: z.array(SessionQuestionSchema).optional(),
    live_profile: z.string().optional(),
    metadata: z.record(z.any()).optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional()
  })
  .passthrough();

export const SessionResponseSchema = z
  .object({
    session_id: z.string(),
    project_id: z.string().optional(),
    script_id: z.string().optional(),
    status: z.string(),
    snapshot: SessionSnapshotSchema,
    questions: z.array(SessionQuestionSchema).optional(),
    metadata: z.record(z.any()).optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional()
  })
  .passthrough();

export const SessionQuestionsResponseSchema = z.object({
  questions: z.array(SessionQuestionSchema)
});

export const SessionAssetsSchema = z.object({
  asset_id: z.string(),
  session_question_id: z.string().nullable(),
  persona_name: z.string().nullable(),
  voice_hint: z.string().nullable().optional(),
  status: z.string(),
  signed_url: z.string().nullable(),
  duration_ms: z.number().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  metadata: z.record(z.any()).optional()
});

export const SessionAssetsResponseSchema = z.object({
  assets: z.array(SessionAssetsSchema),
  current_question_id: z.string().nullable(),
  next_sequence: z.string().nullable(),
  assets_missing: z.array(z.string()).optional(),
  asset_error: z
    .object({
      code: z.string(),
      message: z.string().optional()
    })
    .optional()
});

export const AudioPreviewSchema = z
  .object({
    signed_url: z.string(),
    checksum: z.string(),
    persona: z.string().optional(),
    content_type: z.string().optional(),
    duration_ms: z.number().optional(),
    expires_at: z.string().optional()
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: "Audio preview must contain at least one field"
  })
  .optional();

export const IntroCopySchema = z.object({
  headline: z.string(),
  explainer: z.string(),
  tool_description: z.string(),
  bill_name: z.string(),
  bill_url: z.string()
});

export const ProjectSchema = z.object({
  project_id: z.string(),
  display_name: z.string(),
  status: z.string(),
  active_script_id: z.string().nullable().optional(),
  intro_copy: IntroCopySchema.optional()
});

export const ProjectScriptsResponseSchema = z.object({
  scripts: z.array(
    z.object({
      script_id: z.string(),
      display_name: z.string(),
      status: z.string(),
      question_count: z.number().optional(),
      is_active: z.boolean().optional()
    })
  )
});

export const StreamHandshakeSchema = z.object({
  info: z.object({
    session_id: z.string(),
    profile: z.string().optional(),
    resume_supported: z.boolean().optional(),
    system_instruction: z.string().optional(),
    tooling: z.array(z.string()).optional()
  }),
  composer: z
    .object({
      prompt_tokens: z.number().optional(),
      compose_ms: z.number().optional()
    })
    .optional(),
  resume: z
    .object({
      notice: z.string().optional(),
      chat_chars: z.number().optional(),
      accepted: z.boolean().optional()
    })
    .optional()
});

export const LetterMetadataSchema = z
  .object({
    bill_id: z.string().nullable().optional(),
    district_id: z.string().nullable().optional(),
    ocd_type: z.string().nullable().optional(),
    recipient_email: z.string().nullable().optional(),
    bill_slug: z.string().nullable().optional(),
    companion_letter_id: z.string().nullable().optional(),
    is_primary: z.boolean().nullable().optional()
  })
  .passthrough()
  .optional();

export const LetterSchema = z
  .object({
    letter_id: z.string(),
    session_id: z.string(),
    status: z.string(),
    subject: z.string().nullable().optional(),
    content: z.string().nullable().optional(),
    metadata: LetterMetadataSchema,
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
    version: z.number().nullable().optional()
  })
  .passthrough();

export const LetterArraySchema = z.array(LetterSchema);

export const LetterListSchema = z
  .object({
    items: z.array(LetterSchema).optional(),
    letters: z.array(LetterSchema).optional(),
    next_cursor: z.string().nullable().optional()
  })
  .transform((data) => ({
    items: data.items ?? data.letters ?? [],
    next_cursor: data.next_cursor ?? null
  }));

export const DeliveryRecipientSchema = z.object({
  recipient_id: z.string(),
  legislator_id: z.string(),
  channel: z.string(),
  status: z.string(),
  attempts: z.number(),
  last_attempt_at: z.string().nullable(),
  last_error: z.string().nullable(),
  metadata: z.record(z.any()).optional()
});

const DeliveryJobBaseSchema = z
  .object({
    id: z.string().optional(),
    job_id: z.string().optional(),
    letter_id: z.string(),
    status: z.string(),
    submitted_at: z.string(),
    completed_at: z.string().nullable().optional(),
    recipients: z.array(DeliveryRecipientSchema),
    metadata: z.record(z.any()).optional()
  })
  .superRefine((data, ctx) => {
    if (!data.job_id && !data.id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Delivery job response missing identifier"
      });
    }
  });

export const DeliveryJobSchema = DeliveryJobBaseSchema.transform((data) => ({
  job_id: data.job_id ?? data.id!,
  letter_id: data.letter_id,
  status: data.status,
  submitted_at: data.submitted_at,
  completed_at: data.completed_at ?? null,
  recipients: data.recipients,
  metadata: data.metadata ?? {}
}));

const RepresentativeDetailsSchema = z.object({
  full_name: z.string(),
  chamber: z.string().optional().nullable(),
  party: z.string().optional().nullable(),
  district_label: z.string().optional().nullable(),
  contact_email: z.string().email().optional().nullable(),
  stats: z
    .array(
      z.object({
        label: z.string(),
        value: z.string()
      })
    )
    .optional()
    .default([])
});

export const SessionOnboardingResponseSchema = z
  .object({
    representative: RepresentativeDetailsSchema.optional(),
    reference_data: z
      .object({
        districts: z.array(
          z
            .object({
              ocd_id: z.string(),
              ocd_type: z.string().nullable().optional(),
              shape_id: z.string().nullable().optional(),
              total_hits: z.number().nullable().optional(),
              person_type_distribution: z
                .array(
                  z.object({
                    key: z.string(),
                    doc_count: z.number()
                  })
                )
                .optional(),
              state: z.string().nullable().optional(),
              district_name: z.string().nullable().optional(),
              representative: z.string().nullable().optional(),
              party: z.string().nullable().optional(),
              website: z.string().nullable().optional(),
              staffer_name: z.string().nullable().optional(),
              staffer_email: z.string().nullable().optional(),
              email: z.string().nullable().optional()
            })
            .passthrough()
        )
      })
      .optional()
  })
  .passthrough();
